import * as React from "react";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { Card } from "baseui/card";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { HorizontalSpacer } from "kanopi-components/SpaceWrapper/HorizontalSpacer";
import { Input, SIZE as INPUT_SIZE } from "baseui/input";
import { mergeOverrides, useStyletron } from "baseui";
import { OnChangeParams, Select, Value } from "baseui/select";
import { Pagination } from "baseui/pagination";
import { Row } from "baseui/data-table";
import { Search } from "baseui/icon";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";

import {
  SIZE,
  TableBuilder as BaseTableBuilder,
  TableBuilderColumn,
  TableBuilderColumnProps,
  TableBuilderProps as BaseTableBuilderProps,
  DIVIDER,
} from "baseui/table-semantic";

type TSearchOptions = {
  label: string;
  value: string;
};

export type TTableView = {
  title?: string;
  columnSchema: TableBuilderColumnProps<Row>[];
  data: unknown[];
  pageCount?: number;
  currentPage?: number;
  onPageChange?: (search: string, filterBy, page: number) => void;
  searchOptions?: TSearchOptions[];
  defaultSearchOption?: string;
  showSearchOptions?: boolean;
  showSearchSection?: boolean;
  showPagination?: boolean;
  onSearch?: (text: string, column?: string) => void;
  onSort?: (key: string, order: string) => void;
  loading: boolean;
  searchPlaceholder?: string;
  filterPlaceholder?: string;
  type?: string;
  divider?: keyof typeof DIVIDER;
} & BaseTableBuilderProps<Row>;

export const TableView = (props: TTableView) => {
  const {
    title,
    columnSchema,
    data,
    pageCount,
    currentPage,
    onPageChange,
    searchOptions: searchFilters,
    showSearchOptions = false,
    defaultSearchOption,
    showSearchSection = true,
    showPagination = true,
    onSearch,
    onSort,
    loading,
    searchPlaceholder = "Search here",
    filterPlaceholder = "Filter by",
    type = "LISTING",
    size = SIZE.compact,
    divider = DIVIDER.clean,
    ...restProps
  } = props;
  const [filtered, setFiltered] = React.useState<boolean>(false);
  const [searchText, setSearchText] = React.useState<string>("");
  const [selectedSearchFilter, setSelectedSearchFilter] = React.useState<Value>(
    defaultSearchOption ? [{ value: defaultSearchOption }] : null
  );

  const [, theme] = useStyletron();

  const onPageChangeCallBack = ({
    nextPage,
  }: {
    nextPage: number;
    prevPage: number;
  }) => {
    onPageChange(
      searchText,
      selectedSearchFilter && selectedSearchFilter[0].value,
      nextPage
    );
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    // Trigger search if input is cleared
    if (!e.target.value && filtered) {
      onSearch("", selectedSearchFilter && selectedSearchFilter[0].value);
    }
  };

  const onSearchFilterChange = (params: OnChangeParams) => {
    setSelectedSearchFilter(params.value);
    onSearch(searchText, params.value[0].value);
  };

  const handleSearch = () => {
    if (searchText) {
      setFiltered(true);
    }
    onSearch(searchText, selectedSearchFilter && selectedSearchFilter[0].value);
  };
  return (
    <Card
      overrides={{
        Root: {
          style: {
            width: "100%",
            borderLeftWidth: 0,
            borderRightWidth: 0,
            borderTopWidth: 0,
            borderBottomWidth: 0,
            boxShadow: theme.lighting.shadow400,
            borderTopLeftRadius: theme.borders.radius300,
            borderTopRightRadius: theme.borders.radius300,
            borderBottomLeftRadius: theme.borders.radius300,
            borderBottomRightRadius: theme.borders.radius300,
          },
        },
      }}
    >
      <VerticalSpacer data-testid={`tableView-${type}`} width="100%">
        {title && <HeadingActionsBar heading={title} headingLevel="section" />}
        {showSearchSection && (
          <HorizontalSpacer justifyContent="left">
            <Block display="flex" minWidth="300px">
              <Input
                onChange={onInputChange}
                value={searchText}
                size={INPUT_SIZE.compact}
                placeholder={searchPlaceholder}
                startEnhancer={<Search size={theme.sizing.scale650} />}
                clearable={true}
                overrides={{
                  Input: {
                    props: {
                      "data-testid": "searchInput",
                    },
                  },
                }}
              />
            </Block>
            <Button
              onClick={handleSearch}
              data-testid="searchButton"
              size={INPUT_SIZE.compact}
            >
              Search
            </Button>
            {showSearchOptions && (
              <Block
                data-testid="searchFilter"
                minWidth={[0, theme.sizing.scale3200, theme.sizing.scale3200]}
              >
                <Select
                  size={INPUT_SIZE.compact}
                  clearable={false}
                  searchable={false}
                  options={searchFilters}
                  labelKey="label"
                  valueKey="value"
                  placeholder={filterPlaceholder}
                  onChange={onSearchFilterChange}
                  value={selectedSearchFilter}
                />
              </Block>
            )}
          </HorizontalSpacer>
        )}
        <TableBuilder
          data={data}
          isLoading={loading}
          size={size}
          onSort={onSort}
          divider={divider}
          columnSchema={columnSchema}
          {...restProps}
        />
        {showPagination && (
          <HorizontalSpacer>
            <Pagination
              numPages={pageCount}
              currentPage={currentPage}
              onPageChange={onPageChangeCallBack}
            />
            <></>
          </HorizontalSpacer>
        )}
      </VerticalSpacer>
    </Card>
  );
};

type TableBuilderProps = BaseTableBuilderProps<Row> & {
  loading?: boolean;
  striped?: boolean;
  columnSchema: TableBuilderColumnProps<Row>[];
  onSort?: (key: string, order: string) => void;
};
export const TableBuilder: React.FC<TableBuilderProps> = (props) => {
  const {
    columnSchema,
    data,
    loading,
    size = SIZE.compact,
    overrides,
    striped = true,
    onSort,
    ...restProps
  } = props;
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortAsc, setSortAsc] = React.useState<boolean>(true);
  const [, theme] = useStyletron();
  const headerBorderStyle = {
    borderBottomColor: theme.colors.contentPrimary,
    borderBottomStyle: theme.borders.border600.borderStyle,
    borderBottomWidth: theme.borders.border600.borderWidth,
  };
  const cellStyle = {
    paddingTop: theme.sizing.scale400,
    paddingBottom: theme.sizing.scale400,
    paddingLeft: theme.sizing.scale500,
    paddingRight: theme.sizing.scale500,
    verticalAlign: "middle",
  };
  const handleSort = (id: string) => {
    if (id === sortColumn) {
      setSortAsc((asc) => !asc);
    } else {
      setSortColumn(id);
      setSortAsc(true);
      onSort?.(sortColumn, sortAsc ? "ASC" : "DESC");
    }
  };
  return (
    <BaseTableBuilder
      data={data}
      sortColumn={sortColumn}
      sortOrder={sortAsc ? "ASC" : "DESC"}
      onSort={handleSort}
      isLoading={loading}
      size={size}
      overrides={mergeOverrides(
        {
          TableBodyRow: {
            style: ({ $theme, $rowIndex }) => ({
              backgroundColor:
                $rowIndex % 2
                  ? striped
                    ? $theme.colors.backgroundSecondary
                    : $theme.colors.backgroundPrimary
                  : $theme.colors.backgroundPrimary,
              ":hover": {
                backgroundColor: "none",
              },
            }),
          },
          TableBodyCell: {
            style: () => ({
              wordBreak: "normal",
            }),
          },
        },
        overrides
      )}
      {...restProps}
    >
      {columnSchema.map(({ children, overrides, ...columnProps }, index) => {
        return (
          <TableBuilderColumn
            key={index}
            {...columnProps}
            overrides={mergeOverrides(
              {
                TableHeadCell: {
                  style: {
                    ...headerBorderStyle,
                    ...cellStyle,
                    ...theme.typography.LabelSmall,
                  },
                },
                TableBodyCell: {
                  style: {
                    wordBreak: "normal",
                    ...cellStyle,
                    ...theme.typography.ParagraphSmall,
                  },
                },
              },
              overrides
            )}
          >
            {children}
          </TableBuilderColumn>
        );
      })}
    </BaseTableBuilder>
  );
};
